import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { EntryIconColorId, EntryIconId, RepositoryFile } from '@trove/core';
import {
  Archive,
  BracketsCurly,
  Calendar,
  AddressBook,
  File,
  FileAudio,
  FileCode,
  FileImage,
  FileXls,
  FileText,
  FilePdf,
  FileVideo,
  Folder,
  FolderOpen,
  type Icon,
} from '@phosphor-icons/react';
import { entryIconColorHex } from '../entryColors';
import { entryIconEmoji } from '../entryIcons';
import { fileVisualKind, type FileVisualKind } from '../fileKinds';
import { fileEntry, folderEntry, type FileTreeEntry } from '../fileTree';
import { useImagePreviewUrl } from '../useImagePreviewUrl';
import styles from './FileGlyph.module.css';

export type FileGlyphSize = 'tree' | 'row' | 'sheet';
export type FileGlyphDensity = 'compact' | 'comfy';

const KIND_META: Record<FileVisualKind, { Icon: Icon; tone: string; thumb?: 'image' | 'html' }> = {
  image: { Icon: FileImage, tone: styles.toneImage ?? '', thumb: 'image' },
  html: { Icon: FileCode, tone: styles.toneHtml ?? '', thumb: 'html' },
  pdf: { Icon: FilePdf, tone: styles.tonePdf ?? '' },
  json: { Icon: BracketsCurly, tone: styles.toneJson ?? '' },
  sheet: { Icon: FileXls, tone: styles.toneSheet ?? '' },
  document: { Icon: FileText, tone: styles.toneText ?? '' },
  audio: { Icon: FileAudio, tone: styles.toneCode ?? '' },
  video: { Icon: FileVideo, tone: styles.toneHtml ?? '' },
  archive: { Icon: Archive, tone: styles.toneFile ?? '' },
  contact: { Icon: AddressBook, tone: styles.toneText ?? '' },
  calendar: { Icon: Calendar, tone: styles.toneSheet ?? '' },
  markdown: { Icon: FileText, tone: styles.toneText ?? '' },
  text: { Icon: FileText, tone: styles.toneText ?? '' },
  code: { Icon: FileCode, tone: styles.toneCode ?? '' },
  binary: { Icon: File, tone: styles.toneFile ?? '' },
};

function glyphPx(size: FileGlyphSize, density: FileGlyphDensity, kind: 'icon' | 'thumbIcon') {
  if (size === 'sheet') return kind === 'icon' ? 22 : 18;
  if (size === 'tree') return kind === 'icon' ? 14 : 12;
  if (density === 'comfy') return kind === 'icon' ? 16 : 14;
  return kind === 'icon' ? 15 : 13;
}

function colorStyle(colorOverride?: EntryIconColorId): CSSProperties | undefined {
  const hex = entryIconColorHex(colorOverride);
  if (!hex) return undefined;
  return { color: hex };
}

function useLazyVisible<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || visible) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setVisible(true);
        observer.disconnect();
      },
      { rootMargin: '120px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  return { ref, visible };
}

function thumbClassName(size: FileGlyphSize, density: FileGlyphDensity) {
  if (size === 'sheet') return styles.thumbSheet;
  if (size === 'tree') return styles.thumbTree;
  return density === 'comfy' ? styles.thumbRowComfy : styles.thumbRow;
}

function ImageThumb({
  file,
  size,
  density,
}: {
  file: RepositoryFile;
  size: FileGlyphSize;
  density: FileGlyphDensity;
}) {
  const { ref, visible } = useLazyVisible<HTMLSpanElement>();
  const [failed, setFailed] = useState(false);
  const preview = useImagePreviewUrl(visible && !failed ? file : undefined);
  const meta = KIND_META.image;
  const Icon = meta.Icon;
  const broken = failed || preview.failed || (visible && !preview.loading && !preview.src);

  if (broken) {
    return <Icon className={`${styles.icon} ${meta.tone}`} size={glyphPx(size, density, 'icon')} />;
  }

  return (
    <span ref={ref} className={`${styles.thumb} ${thumbClassName(size, density)}`}>
      {preview.src ? (
        <img
          src={preview.src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        <Icon
          className={`${styles.icon} ${meta.tone}`}
          size={glyphPx(size, density, 'thumbIcon')}
        />
      )}
    </span>
  );
}

function HtmlThumb({
  file,
  size,
  density,
}: {
  file: RepositoryFile;
  size: FileGlyphSize;
  density: FileGlyphDensity;
}) {
  const { ref, visible } = useLazyVisible<HTMLSpanElement>();
  const html =
    !file.content || file.content.startsWith('data:') ? undefined : file.content.slice(0, 12_000);

  if (!html) {
    return (
      <FileCode
        className={`${styles.icon} ${styles.toneHtml}`}
        size={glyphPx(size, density, 'icon')}
      />
    );
  }

  return (
    <span
      ref={ref}
      className={`${styles.thumb} ${styles.htmlThumb} ${thumbClassName(size, density)}`}
    >
      {visible ? (
        <iframe
          className={styles.htmlFrame}
          title=""
          sandbox=""
          tabIndex={-1}
          srcDoc={html}
          loading="lazy"
        />
      ) : (
        <FileCode
          className={`${styles.icon} ${styles.toneHtml}`}
          size={glyphPx(size, density, 'thumbIcon')}
        />
      )}
    </span>
  );
}

export function FileGlyph({
  entry,
  open,
  size = 'row',
  density = 'comfy',
  showPreview = true,
  iconOverride,
  colorOverride,
}: {
  entry: FileTreeEntry | { type: 'folder'; name: string; path: string };
  open?: boolean;
  size?: FileGlyphSize;
  density?: FileGlyphDensity;
  showPreview?: boolean;
  iconOverride?: EntryIconId;
  colorOverride?: EntryIconColorId;
}) {
  const tint = colorStyle(colorOverride);
  const overrideEmoji = entryIconEmoji(iconOverride);
  if (overrideEmoji) {
    return (
      <span
        className={`${styles.emoji} ${styles[`emoji${size[0]!.toUpperCase()}${size.slice(1)}`] ?? ''}`}
        style={tint ? { backgroundColor: tint.color } : undefined}
        aria-hidden="true"
      >
        {overrideEmoji}
      </span>
    );
  }

  if (entry.type === 'folder') {
    const Icon = open ? FolderOpen : Folder;
    return (
      <Icon
        className={`${styles.icon} ${styles.toneFolder}`}
        size={glyphPx(size, density, 'icon')}
        style={tint}
      />
    );
  }

  const meta = KIND_META[fileVisualKind(entry.file)];
  if (showPreview && meta.thumb === 'image' && !tint) {
    return <ImageThumb file={entry.file} size={size} density={density} />;
  }
  if (showPreview && meta.thumb === 'html' && !tint) {
    return <HtmlThumb file={entry.file} size={size} density={density} />;
  }

  const Icon = meta.Icon;
  return (
    <Icon
      className={`${styles.icon} ${meta.tone}`}
      size={glyphPx(size, density, 'icon')}
      style={tint}
    />
  );
}

export function FileSheetGlyph({
  file,
  iconOverride,
  colorOverride,
}: {
  file: RepositoryFile;
  iconOverride?: EntryIconId;
  colorOverride?: EntryIconColorId;
}) {
  return (
    <FileGlyph
      entry={fileEntry(file)}
      size="sheet"
      density="comfy"
      iconOverride={iconOverride}
      colorOverride={colorOverride}
    />
  );
}

export function FolderSheetGlyph({
  path,
  iconOverride,
  colorOverride,
}: {
  path: string;
  iconOverride?: EntryIconId;
  colorOverride?: EntryIconColorId;
}) {
  return (
    <FileGlyph
      entry={folderEntry(path)}
      size="sheet"
      density="comfy"
      iconOverride={iconOverride}
      colorOverride={colorOverride}
    />
  );
}
